import React from 'react';
import {ProcedureItemsCtx} from 'components/dataProviders/procedureItems';
import {CircularProgress} from '@material-ui/core';
import Image from 'material-ui-image';
import {Check} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import {contrastFontColor} from 'controllers/Utils';

const style = makeStyles((theme) => ({
    withText:
    {
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        '&.horizontal':
        {
            height: 40,
            flexDirection: 'row'
        }
    },
    text: {
        flexGrow: 1,
        textAlign: 'center',
        borderRadius: 4,
        lineHeight: 'initial',
        margin: '3px 0',
        padding: 2
    },
    icon:
    {
        width: 40,
        height: 40,
        float: "left",
        position: 'relative'
    },
    fullWidth:
    {
        float: "none"
    },
    iconImage:
    {
        width: '100%',
        height: '100%'
    },
    signalIcon:
    {
        width: '60%',
        height: '60%',
        left: '20%',
        top: "20%",
        position: 'absolute',
        zIndex: 1000
    },
    signalBackground:
    {
        width: '100%',
        height: '100%',
        zIndex: 0,
        position: 'absolute',
        backgroundSize: 'contain'
    },
    signalForeground: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        zIndex: 1100,
        backgroundSize: 'contain'
    }, 
    signalWarning: {
        backgroundImage: "url(/img/item_warning.png)",
    },
    signalWarningBorder: {
        position: 'absolute',
        width: '100%',
        height: '100%'
    },
    signalWarningIcon: {
        width: '40%',
        height: '40%',
        top: '45%',
        left: '30%',
    },
    signalForbidden: {
        backgroundImage: "url(/img/item_forbidden.png)",
    },
    verificationBadge: {
        width: '50%',
        height: '50%',
        top: '-15%',
        right: '-15%',
        position: 'absolute',
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        zIndex: 1100,
        borderRadius: '50%',
        padding: '5%'
    }
}), {name: "RaymanItem"});

function AsyncImage({iconID, ...props})
{
    
    const [src,setSrc] = React.useState(null);
    const ctx = React.useContext(ProcedureItemsCtx);
    React.useEffect(()=>{
        if(ctx.loading || ctx.icons.loading || !iconID) return;
        ctx.icons.data.indexed[iconID].getUrl().then(url => setSrc(url));
    }, [ctx, iconID]);
    if(!src) return <CircularProgress />
    else return <Image src={src} {...props}/>
}

function AsyncTintedImage({iconID, ...props})
{
    
    const [src,setSrc] = React.useState(null);
    const ctx = React.useContext(ProcedureItemsCtx);
    React.useEffect(()=>{
        if(ctx.loading || ctx.icons.loading || !iconID) return;
        ctx.icons.data.indexed[iconID].getUrl().then(url => setSrc(url));
    }, [ctx, iconID]);
    if(!src) return <CircularProgress />
    else return <TintedImage src={src} {...props}/>
}

function TintedIcon({iconID, color, ...props})
{
    const classes = style();
    props.className = clsx(classes.icon, props.className);
    return <AsyncTintedImage iconID={iconID} color={`#${color}`} {...props}>{props.children}</AsyncTintedImage>
}

function TintedImage({src, color, ...props})
{
    const maskImage = `url(${src})`;
    const maskSize = "contain";
    delete props.iconText;
    if(!src) return <CircularProgress />
    else return <div className={props.className} style={{backgroundColor: color, WebkitMaskImage: maskImage, WebkitMaskSize: maskSize, maskImage: maskImage, maskSize: maskSize}} {...props}>{props.children}</div>
}

function ItemWarning({iconID, color="FFB81E", iconText, ...props})
{
    const classes = style();
    if(!color) color = "FFB81E";
    return <div className={clsx(classes.withText, props.horizontal && ".horizontal")}>
        <div className={clsx(classes.icon, props.className)}>
            <div className={clsx(classes.signalBackground, classes.signalWarning)}>
                <TintedImage src="/img/item_warning_border.png" color={`#${color}`} className={classes.signalWarningBorder}/>
            </div>
            <div className={clsx(classes.signalIcon, classes.signalWarningIcon)}>
                <AsyncImage iconContainerStyle={{transform: 'scale(0.5)'}} color="transparent" iconID={iconID} alt={"icon "+iconID}/>
            </div>
        </div>
        {iconText &&
        <div className={clsx(classes.text)} style={{backgroundColor: `#${color}`, color: contrastFontColor(color)}}>
            {iconText}
        </div>
        }
    </div>
}

function ItemForbidden({iconID, color="B21515", iconText, ...props})
{
    const classes = style();
    if(!color) color = "B21515";

    return <div className={clsx(classes.withText, props.horizontal && ".horizontal")}>
        <div className={clsx(classes.icon, props.className)}>
            <div className={clsx(classes.signalForeground, classes.signalForbidden)}>
                <TintedImage src="/img/item_forbidden.png" color="black" />
            </div>
            <div className={clsx(classes.signalIcon)}>
                <AsyncImage iconContainerStyle={{transform: 'scale(0.5)'}} color="transparent" iconID={iconID} alt={"icon "+iconID}/>
            </div>
        </div>
        {iconText && 
        <div className={clsx(classes.text)} style={{backgroundColor: `#${color}`, color: contrastFontColor(color)}}>
            {iconText}
        </div>
        }
    </div>
}


function ItemVerification({iconID, color, ...props})
{
    const classes = style();

    return <div className={clsx(classes.icon)}>
        <Check className={classes.verificationBadge} />
        <TintedIcon iconID = {iconID} color = {color} {...props}/>
    </div>
}

function ItemIcon({iconID, ...props})
{
    const classes = style();
    return <div className={clsx(classes.icon, props.className)}>
        <AsyncImage iconContainerStyle={{transform: 'scale(0.5)'}} color="transparent" iconID={iconID} alt={"icon "+iconID} className={classes.iconImage}/>
    </div>
}

function ItemCategoryLabel({categoryID, ...props})
{
    const ctx = React.useContext(ProcedureItemsCtx);
    if(!categoryID) return "Sin Categoría";
    if(ctx.loading || ctx.categories.loading) return "...";
    return ctx.categories.data.indexed[categoryID].name;
}

function ItemDrawer({itemID, ...props})
{
    const ctx = React.useContext(ProcedureItemsCtx);
    if(!ctx.items.indexed[itemID]) return null;
    return <ItemPreview typeID={ctx.items.indexed[itemID].typeID} iconID={ctx.items.indexed[itemID].iconID} iconText={ctx.items.indexed[itemID].iconText} color={ctx.items.indexed[itemID].color} {...props} />
}

function ItemPreview({typeID, iconID, iconText, color = "333333", ...props})
{
    // const classes = style();
    switch(typeID)
    {
        case 1: // Default, only tinted icon
        default:
            return <TintedIcon iconID={iconID} color={color} {...props}/>
        case 2: // Verification
            return <ItemVerification iconID={iconID} color={color} iconText={iconText} {...props}/>
        case 3: // Warning
            return <ItemWarning iconID={iconID} color={color} iconText={iconText} {...props}/>
        case 4: // Forbidden
            return <ItemForbidden iconID={iconID} color={color} iconText={iconText} {...props}/>
            
    }
}

export {ItemIcon, ItemCategoryLabel, ItemDrawer, ItemPreview}